import { call, put, select, takeLatest } from 'redux-saga/effects';
import { toast } from 'react-hot-toast';
import {
  FETCH_SALES_RESTAURANTS,
  FETCH_SALES_ANALYTICS,
  fetchSalesRestaurantsSuccess,
  fetchSalesRestaurantsFail,
  fetchSalesAnalyticsSuccess,
  fetchSalesAnalyticsFail,
  fetchSalesAnalyticsSkip,
} from '../actions/salesAnalytics';
import { fetchSalesRestaurantsApi, fetchSalesAnalyticsApi } from '../apis/salesAnalytics';
import {
  buildSalesAnalyticsCacheKey,
  getDefaultRestaurantIds,
  isSalesAnalyticsCacheFresh,
} from '../../utils/salesAnalytics';

const selectSalesAnalyticsState = (state) => state.salesAnalytics || {};

function* fetchSalesRestaurantsSaga(action) {
  const forceRefresh = Boolean(action.payload?.forceRefresh);

  try {
    const sales = yield select(selectSalesAnalyticsState);

    if (!forceRefresh && sales.restaurantsLoaded && sales.restaurants?.length) {
      return;
    }

    const restaurants = yield call(fetchSalesRestaurantsApi);
    yield put(
      fetchSalesRestaurantsSuccess({
        restaurants,
        selectedRestaurantIds: getDefaultRestaurantIds(restaurants),
      })
    );
  } catch (error) {
    yield put(fetchSalesRestaurantsFail(error.message || 'Failed to load restaurants'));
  }
}

function* fetchSalesAnalyticsSaga(action) {
  const {
    startDate,
    endDate,
    restaurantIds = [],
    groupBy = '',
    forceRefresh = false,
    showRefreshToast = false,
  } = action.payload || {};

  if (!restaurantIds.length) {
    yield put(fetchSalesAnalyticsSkip());
    return;
  }

  const cacheKey = buildSalesAnalyticsCacheKey({
    startDate,
    endDate,
    restaurantIds,
    groupBy,
  });

  try {
    const sales = yield select(selectSalesAnalyticsState);
    const cache = sales.cache || {};

    if (!forceRefresh && sales.activeCacheKey === cacheKey && sales.analytics) {
      yield put(fetchSalesAnalyticsSkip());
      return;
    }

    if (!forceRefresh) {
      const cachedEntry = cache[cacheKey];
      if (cachedEntry?.analytics && isSalesAnalyticsCacheFresh(cachedEntry)) {
        yield put(
          fetchSalesAnalyticsSuccess({
            cacheKey,
            analytics: cachedEntry.analytics,
            fromCache: true,
            fetchedAt: cachedEntry.fetchedAt,
            alreadyActive: sales.activeCacheKey === cacheKey,
          })
        );
        return;
      }
    }

    const analytics = yield call(fetchSalesAnalyticsApi, {
      startDate,
      endDate,
      restaurantIds,
      groupBy,
    });

    yield put(
      fetchSalesAnalyticsSuccess({
        cacheKey,
        analytics,
        fromCache: false,
        fetchedAt: Date.now(),
      })
    );

    if (showRefreshToast) {
      toast.success('Sales analytics updated');
    }
  } catch (error) {
    const message = error.message || 'Failed to load sales analytics';
    yield put(fetchSalesAnalyticsFail(message));
    toast.error(message);
  }
}

export default function* salesAnalyticsSaga() {
  yield takeLatest(FETCH_SALES_RESTAURANTS, fetchSalesRestaurantsSaga);
  yield takeLatest(FETCH_SALES_ANALYTICS, fetchSalesAnalyticsSaga);
}
